import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useTranslation } from "react-i18next";
import useFeature from "../../hooks/useFeature";
import { ConfigContext } from "../../utils/ConfigContext";
import {
  clickEventOnFeature,
  renderCell,
  standardizeColor,
} from "../../utils/helper";
import { getLayerSymbology } from "../../utils/symbologies";
import ImageSwiper from "../MobileApp/MobilePopup/PopupBody/ImageSwiper/ImageSwiper";
import {
  StyledFeatureCardBody,
  StyledFeatureCardContainer,
  StyledFeatureCardHeader,
  StyledFeatureImageContainer,
} from "./FeatureCard-styled";

const FeatureCard = ({ feature, layer, onImageLoad }) => {
  const { t, i18n } = useTranslation("common");
  const { config } = useContext(ConfigContext);
  const { title, iconUrl, dateTimeAgo } = useFeature(feature, t, i18n);
  const [images, setImages] = useState([]);
  const [isPending, startTransition] = useTransition();

  const symbologyFields = useMemo(() => {
    const layer = feature.sourceLayer ?? feature.layer;
    const result = [];
    if (layer) {
      const symboloy = getLayerSymbology(layer, config);
      const { colorMap } = symboloy || {};

      if (colorMap) {
        const colorMapFields = colorMap.field
          ? [colorMap.field]
          : colorMap.fields;
        const layerFields = layer.fields.filter((field) =>
          colorMapFields.includes(field.name)
        );

        const colors = layerFields.map((field) => {
          const color =
            colorMap.default[feature.attributes[field.name]] ??
            symboloy.defaultColor;
          const standartColor = standardizeColor(color);
          if (color)
            return {
              value: renderCell(
                field,
                feature.attributes[field.name],
                t,
                config
              ),
              bg: standartColor.includes("#ffffff") ? "#000" : standartColor,
              color: "#fff",
              field: field.name,
              title: t(
                "layer.fieldAlias." + field.name + ".title",
                field.alias
              ),
            };
        });

        result.push(...colors.filter((item) => item));
      }
    }

    return result;
  }, [feature, config, t]);

  useEffect(() => {
    setImages([]);
    if (!layer.capabilities.operations.supportsQueryAttachments) return;

    layer
      .queryAttachments({
        objectIds: [feature.attributes[layer.objectIdField]],
      })
      .then((response) => {
        const attachments = Object.values(response).flat(1);

        startTransition(() => {
          setImages(
            attachments?.filter((att) => att.contentType.startsWith("image")) ||
              []
          );
        });

        requestAnimationFrame(() => {
          if (onImageLoad) {
            onImageLoad();
          }
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }, [feature, layer]);

  const hasReportingUpdate = useMemo(() => {
    return layer.layerConfig?.hasReportingUpdate;
  }, [layer]);

  const handleOpenPopup = () => {
    clickEventOnFeature(feature, {
      // zoom: false,
      // duration: 1,
      showPopup: true,
    });
  };

  return (
    <StyledFeatureCardContainer onClick={handleOpenPopup}>
      <StyledFeatureCardHeader>
        <StyledFeatureImageContainer>
          <img src={iconUrl} />
        </StyledFeatureImageContainer>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            textAlign: "left",
            alignItems: "center",
            gap: "0px",
          }}
        >
          <span>{layer.getLayerTitle(t)}</span>
          <span className="text-elipsis">: {title}</span>
        </div>
      </StyledFeatureCardHeader>
      <StyledFeatureCardBody>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {symbologyFields.length > 0
            ? symbologyFields.map((field, index) => (
                <Fragment key={index}>
                  <span
                    style={{
                      display: "inline-block",
                      color: field.bg,
                      fontSize: 10,
                      fontWeight: 500,
                    }}
                  >
                    {field.value}
                  </span>
                  <span
                    style={{
                      display: "inline-block",
                      width: "2px",
                      height: "2px",
                      borderRadius: "50%",
                      background: "#7d7d7d",
                      margin: "0 6px",
                      verticalAlign: "middle",
                    }}
                  ></span>
                </Fragment>
              ))
            : null}
          {dateTimeAgo && (
            <>
              <span style={{ fontSize: 10 }}>
                {t("screen.widget.LatestUpdates.updatedOn")} {dateTimeAgo}
              </span>
            </>
          )}
        </div>
      </StyledFeatureCardBody>
      <ImageSwiper images={images} />
    </StyledFeatureCardContainer>
  );
};

export default FeatureCard;
